import type { CrowdexSavedAgent } from '@crowdex/agent-contracts';
import { useEffect, useState } from 'react';
import { resolvedAgentCapabilityMode } from '@/lib/agent-runtime/saved-agents';
import { collectPluginSkills, skillReferences } from '@/lib/native-agent/session-meta';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';

export function useAiAgentCapabilities(
  profile: CrowdexSavedAgent | null,
  workspace: string | null,
  cloud: boolean,
) {
  const key = JSON.stringify([
    profile?.id,
    profile?.capabilityMode,
    profile?.skillRefs,
    profile?.mcpServerNames,
    workspace,
    cloud,
  ]);
  const capabilityMode = profile ? resolvedAgentCapabilityMode(profile) : 'inherit';
  const selectedSkillRefs = capabilityMode === 'custom' ? profile?.skillRefs : undefined;
  const selectedMcpServerNames = capabilityMode === 'custom' ? profile?.mcpServerNames : undefined;
  const required = Boolean(selectedSkillRefs?.length || selectedMcpServerNames?.length);
  const [state, setState] = useState({ key: '', instructions: '', error: null as string | null });
  useEffect(() => {
    if (!required) return;
    let cancelled = false;
    async function load() {
      if (cloud)
        throw new Error(
          'This AI agent uses device capabilities. Choose a local client or another AI agent.',
        );
      if (!workspace) throw new Error('Open a workspace to use this AI agent.');
      // MCP availability is validated against the exact per-conversation host
      // snapshot by resolveAgentMcpCapabilities. This hook only names the agent's skills.
      const plugins = await nativePluginClient.list(workspace);
      const skills = collectPluginSkills(plugins);
      for (const ref of selectedSkillRefs ?? []) {
        if (!skills.some((skill) => skill.pluginName === ref.plugin && skill.uri === ref.uri))
          throw new Error(`AI agent skill unavailable: ${ref.name}`);
      }
      // Named, not copied: the workspace server gives the agent each skill whole, from its file.
      return skillReferences(selectedSkillRefs ?? []);
    }
    void load()
      .then((instructions) => {
        if (!cancelled) setState({ key, instructions, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setState({
            key,
            instructions: '',
            error: error instanceof Error ? error.message : String(error),
          });
      });
    return () => {
      cancelled = true;
    };
  }, [key, required, selectedSkillRefs, workspace, cloud]);
  return {
    loading: required && state.key !== key,
    instructions: required && state.key === key ? state.instructions : '',
    error: required && state.key === key ? state.error : null,
  };
}
